<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDrag } from '../../composables/useDrag'
import { usePanels } from '../../composables/usePanels'
import { usePanelHeightManager } from '../../composables/usePanelHeightManager'
import { useWindowCentering } from '../../composables/useWindowCentering'
import { usePanelDragClick } from '../../composables/usePanelDragClick'
import { usePanelPerformance } from '../../composables/usePanelPerformance'
import { usePanelAnimations } from '../../composables/usePanelAnimations'
import Logo from '../Logo.vue'

type Side = 'left' | 'right'

interface Props {
  side: Side
  open: boolean
  title: string
  preview?: boolean
  dynamicPosition?: number | null
  isRouteTransitioning?: boolean
}

interface Emits {
  (e: 'dragOpen', side: Side): void
  (e: 'close'): void
}

const props = withDefaults(defineProps<Props>(), {
  preview: false,
  dynamicPosition: null,
  isRouteTransitioning: false
})

const emit = defineEmits<Emits>()

const route = useRoute()
const { isDragging, dragSide, startDrag } = useDrag()
const { savePanelScroll, getPanelScroll } = usePanels()
const { updateAppHeight } = usePanelHeightManager()

// Animation coordinator
const { executeAnimation, animateRouteTransition, isPanelAnimating, isAnyAnimationRunning } = usePanelAnimations()

// Panel element references
const panelContent = ref<HTMLElement | null>(null)
const panelElement = ref<HTMLElement | null>(null)
const panelTab = ref<HTMLElement | null>(null)

// Computed properties
const isThisPanelDragging = computed(() => dragSide.value === props.side)

// Check if this panel is currently animating
const isAnimating = computed(() => isPanelAnimating(props.side))

// Initialize composables
const {
  isTransitioning,
  isScrolling,
  setPanelTabRef,
  initializeWindowCentering,
  handlePositionTransition,
  updatePanelTabPosition,
  invalidateDOMCache,
  cleanup: cleanupWindowCentering
} = useWindowCentering()

const {
  isPanelOnScreen,
  getDynamicTransform,
  initializePerformanceOptimizations,
  cleanupObservers
} = usePanelPerformance()

function handlePointerStart(e: PointerEvent | MouseEvent | TouchEvent) {
  if (isAnyAnimationRunning()) return
  
  startDrag(e, props.side, props.open, async (shouldOpen: boolean) => {
    if (shouldOpen) {
      emit('dragOpen', props.side)
    } else {
      // Only emit 'close' if the panel was actually open before dragging
      if (props.open) {
        // Save scroll position before closing
        if (panelContent.value) {
          savePanelScroll(props.side, panelContent.value.scrollTop)
        }
        emit('close')
      }
    }
  }, props.preview)
}

async function handleClose() {
  // Prevent multiple close actions
  if (isAnimating.value) return
  
  // Save scroll position before closing
  if (panelContent.value) {
    savePanelScroll(props.side, panelContent.value.scrollTop)
  }
  emit('close')
}

// Initialize drag/click composable
const {
  handleTabPointerDown: handleTabPointerDownComposable,
  cleanup: cleanupDragClick
} = usePanelDragClick({
  onTabClick: async (e: Event) => {
    // Prevent event from bubbling to panel handlers
    e.stopPropagation()
    e.preventDefault()
    
    // Don't handle clicks during animations
    if (isAnyAnimationRunning()) return
    
    if (props.open) {
      await handleClose()
    } else {
      emit('dragOpen', props.side)
    }
  },
  onDragStart: handlePointerStart
})

// Computed styles and classes
const dynamicTransform = computed(() => 
  getDynamicTransform(props.dynamicPosition, props.preview)
)

const panelClasses = computed(() => [
  'side-panel',
  props.side,
  {
    active: props.open,
    dragging: isThisPanelDragging.value,
    preview: props.preview && !props.open && !isThisPanelDragging.value,
    'route-transitioning': props.isRouteTransitioning,
    'animating': isAnimating.value
  }
])

const panelStyles = computed(() => {
  const styles: any = {}
  
  // Apply dynamic transform for preview positioning
  if (dynamicTransform.value && !isAnimating.value) {
    styles.transform = dynamicTransform.value
  }
  
  // Apply route transition transform (higher priority)
  if (props.isRouteTransitioning && !props.open && !isAnimating.value) {
    const extraOffset = props.side === 'left' ? '-110%' : '110%'
    styles.transform = `translateX(${extraOffset}) translateZ(0)`
  }
  
  return styles
})


function handlePanelPreviewClick(e: Event) {
  // if (!props.preview || props.open || isDragging.value || isAnyAnimationRunning()) return
  
  // const target = e.target as HTMLElement
  // if (target?.closest('.panel-tab')) return
  
  // // Open the panel
  // emit('dragOpen', props.side)
}

function handleTabPointerDown(e: PointerEvent | MouseEvent | TouchEvent) {
  // Don't handle if animations are running
  if (isAnyAnimationRunning()) return
  
  handleTabPointerDownComposable(e, isDragging.value)
}

function handleResize() {
  invalidateDOMCache()
  updatePanelTabPosition()
}

function handleVisibilityChange(isVisible: boolean) {
  // Resume/pause scroll handling based on visibility
  if (isVisible) {
    updatePanelTabPosition()
  }
}

// Lifecycle
onMounted(() => {
  if (props.open && panelContent.value) {
    panelContent.value.scrollTop = getPanelScroll(props.side)
  }
  
  setPanelTabRef(panelTab.value)
  initializeWindowCentering()
  
  initializePerformanceOptimizations(
    panelTab.value,
    panelContent.value,
    handleResize,
    handleVisibilityChange
  )
})

// Watchers with GSAP animation coordination
watch(() => props.open, async (isOpen, wasOpen) => {
  if (!panelElement.value) return
  
  // Use GSAP animations instead of CSS-only transitions
  if (isOpen && !wasOpen) {
    // Panel is opening
    await executeAnimation(panelElement.value, props.side, 'open')
  } else if (!isOpen && wasOpen) {
    // Panel is closing
    if (panelContent.value) {
      savePanelScroll(props.side, panelContent.value.scrollTop)
    }
    await executeAnimation(panelElement.value, props.side, 'close')
  }
  
  if (!isOpen && panelElement.value && (panelElement.value as any).__heightManagerCleanup) {
    (panelElement.value as any).__heightManagerCleanup()
    delete (panelElement.value as any).__heightManagerCleanup
  }
  
  if (isOpen) {
    handlePositionTransition('panel-open', {
      shouldInvalidateCache: true
    })
  } else {
    handlePositionTransition('panel-close')
  }
})

// Handle route transitions with GSAP
watch(() => props.isRouteTransitioning, async (isTransitioning, wasTransitioning) => {
  if (!panelElement.value) return
  
  // Use GSAP for route transition animations
  if (isTransitioning && !wasTransitioning) {
    // Route is changing - animate panels out
    await animateRouteTransition(panelElement.value, props.side, false)
  } else if (!isTransitioning && wasTransitioning) {
    // Route change complete - animate panels back in
    await animateRouteTransition(panelElement.value, props.side, true)
  }
})

watch(() => route.path, () => {
  handlePositionTransition('route-change', {
    shouldInvalidateCache: true
  })
})

// Cleanup on unmount
onBeforeUnmount(() => {
  if (panelElement.value && (panelElement.value as any).__heightManagerCleanup) {
    (panelElement.value as any).__heightManagerCleanup()
  }
  
  // Clean up all composables
  cleanupWindowCentering()
  cleanupDragClick()
  cleanupObservers()
  
  if (panelTab.value) {
    const clonedNode = panelTab.value.cloneNode(true)
    panelTab.value.parentNode?.replaceChild(clonedNode, panelTab.value)
  }
})
</script>

<template>
  <div
    ref="panelElement"
    @pointerdown="handlePointerStart"
    @click="handlePanelPreviewClick"
    :class="panelClasses"
    :style="panelStyles"
  >
    <div 
      class="panel-tab" 
      ref="panelTab"
      @pointerdown="handleTabPointerDown"
    >
      <span class="panel-tab-title">
        <span class="panel-tab-indicator">
          <span class="indicator-bar"></span>
          <span class="indicator-bar"></span>
        </span>
        {{ title }}
      </span>
    </div>
    <div class="panel-content top-spaced-" ref="panelContent">
      <div class="logo-close" @click="handleClose">
        <Logo :color="side === 'left' ? 'white' : 'black'" :width="60" :height="60" monogram/>
      </div>
      <slot />

      <!-- 
      <div class="text-centred">
        <Button @click="handleClose" to="#">Close</Button>
      </div>
      -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '../../assets/scss/variables' as *;
  @use '../../assets/scss/mixins' as m;
  @use '../../assets/scss/themes' as *;

  .top-spaced {
    margin-top: 80px;
  }
  .text-centred{
    text-align: center;
    width: 100%;
  }
  .side-panel {
    position: absolute;
    top: 0;
    height: 100%;
    width: calc(100% - 145px);
    z-index: 100;
    will-change: transform;
    backface-visibility: hidden;
    transform: translate3d(-100%, 0, 0);
    transition: transform .3s ease;
    contain: layout style;
    transform-style: preserve-3d;

    &.dragging, &.active{
      transition: none;
    }
    


    &.animating-open {
      z-index: 102;
    }
    
    &.animating-close {
      z-index: 102;
    }
    
    &.animating {
      pointer-events: none;
      
      .panel-tab {
        pointer-events: auto; 
      }
    }

    .logo-close {
      z-index: 110;
      opacity: 0;
      transition: opacity 0.25s ease;
      
      .panel-open &{
        opacity: 1;
      }
    }

    .logo-close,
    .close-button {
      pointer-events: auto;
      z-index: 110;
    }

    @media (max-width: 991px) {
      width: 100%;
      z-index: 50;
      // More aggressive hardware acceleration on mobile
      will-change: transform;
      transform: translate3d(-100%, 0, 0);
      
      // Ensure proper mobile panel behavior - no background scroll
      &.active {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }

    &.preview {
      cursor: pointer !important;
      pointer-events: all;
      // Preview animations handled by GSAP via usePanelPerformance
      
      @media (max-width: 991px) {
        cursor: default !important;
      }

      // Static preview positions - GSAP handles smooth transitions
      &.left {
        transform: translate3d(-80%, 0, 0);
        
        @media (max-width: 991px) {
          transform: translate3d(-100%, 0, 0);
        }
      }
      
      &.right {
        transform: translate3d(80%, 0, 0);
        
        @media (max-width: 991px) {
          transform: translate3d(100%, 0, 0);
        }
      }

      .logo-close {
        pointer-events: auto;
        opacity: 0;
      }
      
      .panel-content {
        pointer-events: none;
      }
    }
    
    &.dragging {
      z-index: 103; // Higher than animating panels to prevent conflicts
      // Maintain hardware acceleration during drag
      will-change: transform;

      @media (max-width: 991px) {
        z-index: 51; // Higher than mobile panels
      }

      .logo-close {
        pointer-events: auto;
        opacity: 0;
      }
      
      .panel-tab {
        cursor: grabbing;
        
        @media (max-width: 991px) {
          cursor: pointer;
        }
      }
    }

    // Panel tab base styles optimized for GSAP
    .panel-tab {
      position: absolute;
      top: 50%;
      transform: translateY(-50%) translateZ(0);
      width: 80px;
      max-width: 80px;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      writing-mode: vertical-lr;
      cursor: grab;
      touch-action: none;
      pointer-events: auto;
      z-index: 101;
      user-select: none;

      //translate: opacity .6s ease;
      
      // Hardware acceleration for GSAP animations
      backface-visibility: hidden;
      will-change: transform, background-color;
      transform-style: preserve-3d;
      transition: background-color .4s ease;

      @media (max-width: 991px) {
        position: fixed;
        bottom: $spacing-lg;
        top: auto;
        transform: translateZ(0);
        height: 50px;
        width: 120px;
        max-width: 120px;
        writing-mode: horizontal-tb;
        border-radius: 25px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        z-index: 60;
        cursor: pointer;
        touch-action: manipulation;
        
      }

      &:hover{
        box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.05);
        
        @media (max-width: 991px) {
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }
      }

      .panel-tab-title {
        user-select: none;
        position: relative;
        // translate: opacity .6s ease;
        top: var(--window-center-offset, 0);
        opacity: var(--tab-title-opacity, 1);
        translate: opacity .6s ease;
        
        
        // GSAP handles all transitions - no CSS transitions needed
        transform: translateZ(0);
        will-change: opacity, transform;
        backface-visibility: hidden;
        transform-style: preserve-3d;
        
        
        @media (max-width: 768px), (prefers-reduced-motion: reduce) {
          transform: translateZ(0) !important;
          top: 0 !important;
          opacity: 1 !important;
          will-change: auto !important;
          --window-center-offset: 0 !important;
          --tab-title-opacity: 1 !important;
        }
        
        @media (max-width: 991px) {
          transform: translateZ(0);
        }
      }
    }

    &.left {
      left: 0;
      border-right: 8px solid var(--theme-panel-left);
      background-color: $xw-teal-medium;

      //new background test
      // backdrop-filter: blur(18px);
      // -webkit-backdrop-filter: blur(18px);
      // background: rgba($xw-teal-medium, 0.8);

      transform: translate3d(-100%, 0, 0);
      
      &.preview:not(.active):not(.dragging) {
        transform: translate3d(-80%, 0, 0);

        @media (max-width: 991px) {
          transform: translate3d(-100%, 0, 0);
        }
      }

      &.active {
        transform: translate3d(0, 0, 0);
        
        @media (max-width: 991px) {
          .panel-tab {
            opacity: 0.7;
          }
        }
      }

      &.dragging .panel-tab {
        background-color: $xw-teal-dark;
      }
      
      .panel-tab {
        left: 100%;
        text-orientation: mixed;
        color: white;
        background-color: $xw-teal-medium;
        border-left: 1px solid rgba($xw-teal-dark, 0.4);
        border-right: 1px solid rgba($xw-teal-dark, 0.4);

        @media (max-width: 991px) {
          left: $spacing-md;
          background-color: var(--theme-panel-left);
        }

        &:hover{
          background-color: $xw-teal-dark;
        }

        .panel-tab-title {
          transform: rotate(180deg) translateZ(0);
          @media (max-width: 991px) {
            transform: translateZ(0);
          }
        }
      }
    }
    
    &.right {
      right: 0;
      border-left: 8px solid var(--theme-panel-right);
      background-color: $xw-yellow;
      
      // //new background test
      // backdrop-filter: blur(18px);
      // -webkit-backdrop-filter: blur(18px);
      // background: rgba($xw-yellow, 0.8);


      transform: translate3d(100%, 0, 0);
      
      .logo-close {
        position: absolute;
        top: 10px;
        left: 50px;
        z-index:110;
      }
      
      &.preview:not(.active):not(.dragging) {
        transform: translate3d(80%, 0, 0);

        @media (max-width: 991px) {
          transform: translate3d(100%, 0, 0);
        }
      }

      &.active {
        transform: translate3d(0, 0, 0);

        @media (max-width: 991px) {
          .panel-tab {
            opacity: 0.7;
          }
        }
      }
      
      &.dragging .panel-tab {
        background-color: $xw-yellow-dark;
      }

      .panel-tab {
        right: 100%;
        color: black;
        background-color: $xw-yellow;
        border-left: 1px solid rgba($xw-yellow-dark, 0.4);
        border-right: 1px solid rgba($xw-yellow-dark, 0.4);

        @media (max-width: 991px) {
          right: $spacing-md;
          background-color: var(--theme-panel-right);
        }

        &:hover {
          background-color: $xw-yellow-dark;
        }
      }
    }
    
    .panel-content {
      padding: $spacing-xl;
      margin: 0 auto;
      height: 100vh;
      min-height: 100vh;
      cursor: default;
      pointer-events: auto;
      position: relative;
      overflow-y: auto;
      overflow-x: hidden;
      box-sizing: border-box;
      
      // Completely hide scrollbars while maintaining scroll functionality
      scrollbar-width: none; // Firefox
      -ms-overflow-style: none; // IE/Edge
      
      // Hide WebKit scrollbars completely
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
      }
      
      &::-webkit-scrollbar-thumb {
        display: none !important;
      }
      
      &::-webkit-scrollbar-track {
        display: none !important;
      }
      
      // Optimize scrolling performance
      -webkit-overflow-scrolling: touch;
      
      // GPU acceleration for scrolling content
      transform: translateZ(0);
      will-change: scroll-position;
      contain: layout style;
      
      @include m.mobile {
        padding: $spacing-md;
      }
      
      .logo-close {
        position: fixed;
        top: 10px;
        right: 50px;
        cursor: pointer;
        z-index: 110;
        
        // Hardware accelerated for GSAP animations
        transform: translateZ(0);
        backface-visibility: hidden;
        will-change: transform, opacity;
        transform-style: preserve-3d;
        
        @media (max-width: 991px) {
          right: 20px;
          top: 20px;
          z-index: 70;
        }
        
        
      }
    }
  }

  // Indicator animations optimized for GSAP
  .panel-tab-indicator {
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translate3d(0, -50%, 0);
    height: 20px;
    width: 10px;
  }

  .indicator-bar {
    width: 4px;
    height: 20px;
    background-color: rgba(255, 255, 255, 1);
    position: absolute;
    transform-origin: center center;
    opacity: 0.3;
    
    // Hardware acceleration for GSAP animations
    backface-visibility: hidden;
    will-change: transform, opacity;
    transform-style: preserve-3d;
  }

  .indicator-bar:first-child {
    top: -10px;
  }

  .indicator-bar:last-child {
    top: 10px;
  }

  // Optimized hover states with 3D transforms
  .side-panel.left .panel-tab:hover .panel-tab-indicator .indicator-bar:first-child {
    transform: rotate3d(0, 0, 1, 10deg);
    opacity: 0.4;
  }

  .side-panel.left .panel-tab:hover .panel-tab-indicator .indicator-bar:last-child {
    transform: rotate3d(0, 0, 1, -10deg);
    opacity: 0.4;
  }

  .side-panel.right .panel-tab:hover .panel-tab-indicator .indicator-bar:first-child {
    transform: rotate3d(0, 0, 1, 10deg);
    opacity: 0.4;
  }

  .side-panel.right .panel-tab:hover .panel-tab-indicator .indicator-bar:last-child {
    transform: rotate3d(0, 0, 1, -10deg);
    opacity: 0.4;
  }

  /* Enhanced active states with 3D transforms */
  .side-panel.left.dragging  .panel-tab-indicator .indicator-bar:first-child,
  .side-panel.left.preview   .panel-tab-indicator .indicator-bar:first-child,
  .side-panel.right.dragging .panel-tab-indicator .indicator-bar:first-child,
  .side-panel.right.preview  .panel-tab-indicator .indicator-bar:first-child {
    transform: rotate3d(0, 0, 1, 10deg);
    opacity: 0.4;
  }

  .side-panel.left.dragging  .panel-tab-indicator .indicator-bar:last-child,
  .side-panel.left.preview   .panel-tab-indicator .indicator-bar:last-child,
  .side-panel.right.dragging .panel-tab-indicator .indicator-bar:last-child,
  .side-panel.right.preview  .panel-tab-indicator .indicator-bar:last-child {
    transform: rotate3d(0, 0, 1, -10deg);
    opacity: 0.4;
  }

  // Additional performance optimizations for iOS/Safari
  @supports (-webkit-touch-callout: none) {
    .side-panel {
      // iOS-specific optimizations
      -webkit-transform: translate3d(-100%, 0, 0);
      -webkit-backface-visibility: hidden;
      
      &.left {
        &.preview:not(.active):not(.dragging) {
          -webkit-transform: translate3d(-80%, 0, 0);
        }
        &.active {
          -webkit-transform: translate3d(0, 0, 0);
        }
      }
      
      &.right {
        -webkit-transform: translate3d(100%, 0, 0);
        
        &.preview:not(.active):not(.dragging) {
          -webkit-transform: translate3d(80%, 0, 0);
        }
        &.active {
          -webkit-transform: translate3d(0, 0, 0);
        }
      }
    }
  }
</style>